/**
 * A stored send hour (0-23) as staff would say it. The survey and Distro both hold the 24-hour
 * number; only this label is 12-hour, so the two ends that people read are each in the notation
 * that suits them.
 */
export function hourLabel(hour: number): string {
  const meridiem = hour < 12 ? 'AM' : 'PM';
  // 0 and 12 both come out of the modulo as 0, and both read as 12.
  return `${hour % 12 === 0 ? 12 : hour % 12}:00 ${meridiem}`;
}

// Deliberately looser than the server's check: this only exists to catch typos before a round
// trip. `normalizeEmail` in lib/messaging/util.ts is what actually decides, and its rejection comes
// back as a toast.
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Why an entry was rejected, rather than the sentence saying so — the wording lives in
 * `ai_coach.summary_settings.entry_errors`, which only the component can look up.
 */
export type EntryError = 'invalid_email' | 'duplicate';

/**
 * The recipient list with whatever is in the summary settings input appended, or the reason it
 * can't be.
 *
 * Kept pure, and in its own module, so Add and Save can share it — Save can't rely on Add's
 * `setRecipients` having landed yet — and so it's testable without pulling the whole API module in.
 */
export function withEntry(recipients: string[], raw: string): { recipients: string[] } | { error: EntryError } {
  const candidate = raw.trim();
  if (!candidate) {
    return { recipients };
  }
  if (!LOOKS_LIKE_EMAIL.test(candidate)) {
    return { error: 'invalid_email' };
  }
  if (recipients.some((recipient) => recipient.toLowerCase() === candidate.toLowerCase())) {
    return { error: 'duplicate' };
  }
  return { recipients: [...recipients, candidate] };
}
